import { Check, Star } from 'lucide-react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { type PhotoPick, photoSrc } from './map-place'

interface PhotoSelectGridProps {
  photos: PhotoPick[]
  coverUrl: string | null
  onToggle: (url: string) => void
  onCover: (url: string) => void
}

export function PhotoSelectGrid({ photos, coverUrl, onToggle, onCover }: PhotoSelectGridProps) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-3">
      {photos.map(({ photo, selected }) => {
        const isCover = selected && photo.sourceUrl === coverUrl
        return (
          <div
            key={photo.sourceUrl}
            className={cn(
              'squircle group relative aspect-square overflow-hidden rounded-md bg-muted ring-offset-background transition-[box-shadow,opacity] duration-200 ease-spring',
              isCover && 'ring-2 ring-brand ring-offset-2',
            )}
          >
            <button
              type="button"
              onClick={() => onToggle(photo.sourceUrl)}
              aria-pressed={selected}
              aria-label={selected ? m.placeWebDeselectPhoto() : m.placeWebSelectPhoto()}
              className="block size-full cursor-pointer outline-none focus-visible:ring-[3px] focus-visible:ring-brand/40"
            >
              <img
                src={photoSrc(photo)}
                alt=""
                className={cn(
                  'size-full object-cover transition-[filter,opacity,transform] duration-200 ease-spring group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100',
                  selected ? 'opacity-100' : 'opacity-40 grayscale',
                )}
              />
            </button>
            <span
              aria-hidden
              className={cn(
                'pointer-events-none absolute top-1 left-1 flex size-4.5 items-center justify-center rounded-full border transition-colors duration-150',
                selected
                  ? 'border-transparent bg-brand text-brand-foreground'
                  : 'border-card/70 bg-foreground/20 text-transparent',
              )}
            >
              <Check className="size-3" strokeWidth={3} />
            </span>
            {selected && (
              <button
                type="button"
                onClick={() => onCover(photo.sourceUrl)}
                aria-label={m.placeWebSetCover()}
                aria-pressed={isCover}
                title={m.placeWebSetCover()}
                className={cn(
                  'absolute top-1 right-1 flex size-6 cursor-pointer items-center justify-center rounded-full outline-none transition-[opacity,background-color,transform] duration-150 ease-spring focus-visible:ring-[3px] focus-visible:ring-brand/40 active:scale-95',
                  isCover
                    ? 'bg-card text-brand opacity-100 shadow-sm'
                    : 'bg-foreground/35 text-card opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
                )}
              >
                <Star className="size-3" fill={isCover ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
